import { useRef, useState } from 'react';
import { FormikHelpers } from 'formik';

import BaseChallenge from './BaseChallenge';
import { useConversation } from 'hooks/conversation';
import { useVendorCommand } from 'hooks/useVendorCommand';
import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';

import { startTimer } from './utils';
import { isLastMessage, isMessageRead } from 'utils/twilio';
import { scrollToMessagesListBottom } from 'utils/message';

import {
  RatingContactFollowUpChallengeFormType,
  RatingContactFollowUpChallengeProps,
} from './types';

import { RESEND_CODE_RETRY_MS, THANK_YOU_VERIFIED_TEXT } from './constants';
import { MILLISECONDS_PER_SECOND } from 'constants/date';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_rating_contact_followup_text_only_question_form (appears after question submission) *
 */
const Challenge = (props: RatingContactFollowUpChallengeProps) => {
  const { twilioMessageIndex, form } = props;

  const previousCode = form.info?.code;
  const initialValues: RatingContactFollowUpChallengeFormType = {
    code: previousCode ?? '',
  };

  const isFormPreviouslyCompleted = form.isCompleted || previousCode;

  const { conversationState } = useConversation();
  const { runVendorCommandAsync } = useVendorCommand();

  // NOTE: API returns msg like `You've had too many failed attempts. Please try again in 20 minutes.` but product has
  // decided to ignore this message and show static text instead. Backend does not actually enforce said timeout.
  const [isMaxAttempts, setIsMaxAttempts] = useState(false);
  const [resendCodeTimer, setResendCodeTimer] = useState(0);

  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const read = isMessageRead(twilioMessageIndex, conversationState);
  const animate = isLatestMessage && !read;

  const twilioMessageSid = conversationState.messages[twilioMessageIndex].sid;

  // Used to update resend code timer every second
  const resendPoll = useRef<NodeJS.Timer>();
  const clearResendPollTimeout = useRef<NodeJS.Timer>();

  const startResendCodeInterval = () => {
    if (!resendPoll) return;

    clearInterval(resendPoll.current);
    resendPoll.current = setInterval(() => {
      setResendCodeTimer((prev) => {
        return prev - MILLISECONDS_PER_SECOND;
      });
    }, MILLISECONDS_PER_SECOND);
  };

  const startResendCodeTimer = (clearMs: number) => {
    startTimer(
      clearResendPollTimeout,
      () => {
        clearInterval(resendPoll.current);
        setResendCodeTimer(0);
      },
      clearMs,
    );
  };

  const onSubmit = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>,
  ) => {
    helpers.setSubmitting(true);
    const result = await runVendorCommandAsync(
      VENDOR_COMMANDS.CARVANA_RATING_CONTACT_FOLLOWUP_VERIFICATION,
      {
        form_id: props.form.id,
        twilio_message_id: twilioMessageSid,
        code: values.code,
      },
    );
    helpers.setStatus('submitted');
    const errorMessage = result?.data?.errors?.code?.error_message || '';
    if (errorMessage.includes('too many failed attempts')) setIsMaxAttempts(true);
    if (errorMessage) helpers.setStatus(errorMessage);
    setTimeout(() => {
      scrollToMessagesListBottom();
    }, 500);
    helpers.setSubmitting(false);
  };

  const onResendCode = async () => {
    await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOWUP_VERIFICATION_RESEND, {
      form_id: props.form.id,
      twilio_message_id: twilioMessageSid,
    });
    setIsMaxAttempts(false);
    setResendCodeTimer(RESEND_CODE_RETRY_MS);
    startResendCodeInterval();
    startResendCodeTimer(RESEND_CODE_RETRY_MS);
  };

  if (isFormPreviouslyCompleted) {
    return <PlainTextMessage text={THANK_YOU_VERIFIED_TEXT} />;
  }

  return (
    <BaseChallenge
      animate={animate}
      initialValues={{ code: previousCode ?? '' }}
      isMaxAttempts={isMaxAttempts}
      twilioMessageIndex={twilioMessageIndex}
      resendCodeTimer={resendCodeTimer}
      onSubmit={onSubmit}
      onResendCode={onResendCode}
    />
  );
};

export default Challenge;
